/// <amd-module name="Wiki/Renderer/TemplateProcessor" />
import * as MarkdownIt from "markdown-it";

import { endsWith, format } from "VSS/Core/Util/String";
import { MarkdownConstants } from "VSS/Features/Markdown/MarkdownConstants";
import { MarkdownRenderer } from "VSS/Features/Markdown/MarkdownRenderer";
import { ContainerOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { IVssPageContext } from "VSS/Platform/Context";

import * as VCContracts from "Wiki/Clients/VC/Contracts/Git";
import { getWikiClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import * as Resources from "Wiki/Renderer/Resources";
import { IWikiMarkdownRendererProps } from "Wiki/Renderer/WikiMarkdownRenderer.Props";
import { FormatConstants, TemplateConstants } from "Wiki/ViewCommon/Constants";
import { decodeHyphenAndSpacesInPath, isInclude, removeMdExtensionFromPagePath } from "Wiki/ViewCommon/Utils/PathHelper";

export interface TemplateHost {
    _updateContent(updatedContent: string): void;
    getProps(): IWikiMarkdownRendererProps;
    createMarkdownRenderer(): MarkdownRenderer;
}

export class TemplateProcessor implements ContainerOptions {
    private get _props(): IWikiMarkdownRendererProps {
        return this._wikiMarkdownRendererBridge.getProps();
    }

    private get _wikiPagePath(): string {
        return this._props.pagePath || "";
    }

    private _templateDefRE: RegExp;
    // Renderer is required for parsing contents from templatefiles to identify template calls inside it
    private _renderer: MarkdownRenderer | undefined = undefined;
    private _isPageTemplateFree = true;
    private _templateContentProvider: TemplatesContentProvider;

    public name = TemplateConstants.Identifier;
    public marker = TemplateConstants.Marker;

    constructor(private _pageContext: IVssPageContext, private _wikiMarkdownRendererBridge: TemplateHost) {
        this._templateDefRE = new RegExp(`^\\s*(${this.marker}{3})?\\s*${this.name}\\s+([^\\s]+)\\s*$`);
        const wikiIdentifier = this._props.wiki
            ? this._props.wiki.id
            : this._props.wikiIdentifier
            ? this._props.wikiIdentifier
            : this._props.repositoryId;
        this._templateContentProvider = new TemplatesContentProvider(
            this._pageContext,
            this._props.projectId,
            wikiIdentifier,
            this._props.wikiVersion,
            this._templateDefRE,
            this._getRenderer,
            this._updateMdRendererContent
        );
    }

    public render = (tokens: MarkdownIt.Token[], idx: number): string => {
        if (tokens[idx].type === TemplateConstants.TemplateOpenTag) {
            // detects template opening tag
            this._handleTemplateFreePage(tokens);
            tokens = _modifyContainerTokens(tokens, idx);
        } else {
            // called when template closing tag is detected
            const containerstartIndex = _findStartIndex(tokens, idx);
            const templateName: string = _extractTemplateName(this._templateDefRE, tokens[containerstartIndex].info);
            this._templateContentProvider.downloadTemplate(templateName);
        }
        return `<div>${tokens[idx].markup}${tokens[idx].info}</div>`;
    };

    public fetchUpdatedMarkdown = (pageContent?: string, wikiPagePath?: string): string | undefined => {
        if (!pageContent || !wikiPagePath) {
            return undefined;
        }

        this._templateContentProvider.updateTemplateContentAndSourceMap(this._getRenderer().parse(pageContent), wikiPagePath);

        if (this._templateContentProvider.isSourceMapForTemplateEmpty(wikiPagePath)) {
            this._isPageTemplateFree = true;
            return undefined;
        } else if (!this._templateContentProvider.areAllTemplatesDownloaded()) {
            this._templateContentProvider.downloadPendingTemplates();
            return undefined;
        }

        return this._templateContentProvider.constructContent(pageContent, wikiPagePath);
    };

    public validate = (params: string): boolean => {
        return Boolean(params.match(this._templateDefRE));
    };

    private _getRenderer = (): MarkdownRenderer => {
        if (!this._renderer) {
            // renderingOptions.containerOptions is not defined while forming constructor, hence initializing here
            this._renderer = this._wikiMarkdownRendererBridge.createMarkdownRenderer();
        }

        return this._renderer;
    };

    private _handleTemplateFreePage = (tokens: MarkdownIt.Token[]): void => {
        // when a page with templates made template-free and in later edits some templates are added
        // need to update Markdown Renderer content if needed here as, otherwise it is called only when a new template is downloaded
        // and WikiMarkdownRenderer does not know the the page has templates
        if (this._isPageTemplateFree) {
            this._isPageTemplateFree = false;
            this._templateContentProvider.updateTemplateContentAndSourceMap(tokens, this._wikiPagePath);
            this._updatedMdRendererContentIfNeeded();
        }
    };

    private _updatedMdRendererContentIfNeeded = (): void => {
        // Update Markdown Renderer Content only when all templates are downloaded
        if (this._templateContentProvider.areAllTemplatesDownloaded()) {
            this._updateMdRendererContent();
        }
    };

    private _updateMdRendererContent = (): void => {
        this._wikiMarkdownRendererBridge._updateContent(this._templateContentProvider.constructContent(this._props.content, this._wikiPagePath, ""));
    };
}

class TemplatesContentProvider {
    private _templateContentMap: { [key: string]: string | undefined } = {};
    private _templatesRequested: { [key: string]: boolean } = {};

    // maintains line numbers of template opening and closing tag in content of templatefile and page source
    private _templateSourceMap: { [key: string]: number[][] } = {};
    private _cycleDetectionHelper: TemplatesCycleDetectionHelper;

    constructor(
        private _pageContext: IVssPageContext,
        private _projectId: string,
        private _wikiIdentifier: string,
        private _wikiVersion: VCContracts.GitVersionDescriptor | undefined,
        private _templateDefRE: RegExp,
        private _markdownRenderer: () => MarkdownRenderer,
        private _onAllTemplatesDownloaded: () => void
    ) {
        this._cycleDetectionHelper = new TemplatesCycleDetectionHelper(this._templateDefRE);
    }

    /**
     * For a given template, constructs source with parameters placed and nested templates replaced.
     * @param templateContent content of template.
     * @param templateName Template file name.
     * @param parameters Paramaters with which the given template is called.
     */
    public constructContent = (templateContent: string, templateName: string, parameters: string = ""): string => {
        let lines: (string | undefined)[] = _replaceParameterValues(templateContent, parameters, this._markdownRenderer()).split("\n");
        if (templateName in this._templateSourceMap) {
            const nestedTemplatesCount = this._templateSourceMap[templateName].length;
            for (let mapIndex = 0; mapIndex < nestedTemplatesCount; mapIndex++) {
                let childTemplateParameters = "";
                const childTemplateLines = this._templateSourceMap[templateName][mapIndex];
                const childTemplateLinesStart = lines[childTemplateLines[0]];
                if (childTemplateLinesStart) {
                    for (let index = childTemplateLines[0] + 1; index < childTemplateLines[1]; index++) {
                        childTemplateParameters += lines[index] + "\n";
                        lines[index] = undefined;
                    }
                    const childTemplateName = _extractTemplateName(this._templateDefRE, childTemplateLinesStart);
                    lines[childTemplateLines[0]] = this.constructContent(
                        this._templateContentMap[childTemplateName] || "",
                        childTemplateName,
                        childTemplateParameters
                    );
                    lines[childTemplateLines[1]] = undefined;
                }
            }

            lines = lines.filter(line => line !== undefined);
        }

        return lines.join("\n");
    };

    public downloadTemplate = (templateName: string): void => {
        // if download request is not already sent for this template
        if (!this._templatesRequested[templateName]) {
            // add this file in the list of unprocessed files
            this._setTemplateContent(templateName, undefined);
            this._templatesRequested[templateName] = true;

            if (isInclude(templateName)) {
                getWikiClient(this._pageContext)
                    .getPageText(
                        this._projectId,
                        this._wikiIdentifier,
                        removeMdExtensionFromPagePath(decodeHyphenAndSpacesInPath(templateName)),
                        VCContracts.VersionControlRecursionType.None,
                        this._wikiVersion,
                        true
                    )
                    .then(
                        (templateContent: string) => {
                            if (templateContent) {
                                if (!this._templateContentMap[templateName]) {
                                    this._setTemplateContent(templateName, templateContent);
                                }
                                const nestedTemplateTokens = _extractTemplateTokensFromMD(this._markdownRenderer(), templateContent);
                                if (nestedTemplateTokens.length) {
                                    // add edges and detect cycle
                                    this._cycleDetectionHelper.addEdges(nestedTemplateTokens, templateName, (nestedTemplateName: string) => {
                                        this._setTemplateContent(
                                            nestedTemplateName,
                                            _getErrorMessageContent(format(Resources.TemplatesCycleDetectedMessage, nestedTemplateName))
                                        );
                                        this._templateSourceMap[nestedTemplateName] = [];
                                    });

                                    // process content to check if any template definitions are present
                                    this._downloadNestedTemplates(nestedTemplateTokens);

                                    // if cycle is detected (in addEdges method), we add empty entry in source map
                                    if (!(templateName in this._templateSourceMap)) {
                                        this._addTemplatesEntriesToSourceMap(templateName, nestedTemplateTokens);
                                    }
                                }

                                this._notifyIfAllTemplatesDownloaded();
                            }
                        },
                        () => {
                            this._setTemplateContent(templateName, _getErrorMessageContent(format(Resources.TemplateNotFoundMessage, templateName)));
                            this._notifyIfAllTemplatesDownloaded();
                        }
                    );
            } else {
                this._setTemplateContent(templateName, _getErrorMessageContent(format(Resources.TemplateNotFoundMessage, templateName)));
                this._notifyIfAllTemplatesDownloaded();
            }
        }
    };

    public areAllTemplatesDownloaded = (): boolean => {
        for (const key in this._templateContentMap) {
            if (!this._templateContentMap[key]) {
                return false;
            }
        }
        return true;
    };

    public downloadPendingTemplates = (): void => {
        for (const key in this._templateContentMap) {
            this.downloadTemplate(key);
        }
    };

    public isSourceMapForTemplateEmpty = (templateName: string): boolean => {
        const sourceMap = this._templateSourceMap[templateName];
        return sourceMap && sourceMap.length === 0;
    };

    // detects all template definitions from markup to keep track of files that need to be processed
    public updateTemplateContentAndSourceMap = (tokens: MarkdownIt.Token[], wikiPagePath: string): void => {
        const templateTokens = _extractTemplateTokens(tokens);
        this._addTemplatesEntriesToSourceMap(wikiPagePath, templateTokens);
        templateTokens.forEach((token: MarkdownIt.Token) => {
            const templateFileName = _extractTemplateName(this._templateDefRE, token.info);
            if (!(templateFileName in this._templateContentMap)) {
                this._templateContentMap[templateFileName] = undefined;
            }
        });
    };

    private _notifyIfAllTemplatesDownloaded = (): void => {
        // rerender only when templates are downloaded
        if (this.areAllTemplatesDownloaded()) {
            this._onAllTemplatesDownloaded();
        }
    };
    /**
     * Adds entry to templateSourceMap for given template using tokens.
     * @param fileName template/page name.
     * @param tokens tokens corresponding to fileName.
     */
    private _addTemplatesEntriesToSourceMap = (templateName: string, templateTokens: MarkdownIt.Token[]): void => {
        this._templateSourceMap[templateName] = templateTokens.map(token => token.map);
    };

    private _downloadNestedTemplates = (templateTokens: MarkdownIt.Token[]): void => {
        templateTokens.forEach((token: MarkdownIt.Token) => {
            const templateName = _extractTemplateName(this._templateDefRE, token.info);
            if (!this._templateContentMap[templateName]) {
                // this will initiate reading content from file as well as processing content of that file
                this.downloadTemplate(templateName);
            }
        });
    };

    private _setTemplateContent(templateName: string, content: string | undefined): void {
        this._templateContentMap[templateName] = content;
    }
}

class TemplatesCycleDetectionHelper {
    // to store call graphs of nested templates
    private _edges: { [key: string]: string[] } = {};
    constructor(private _templateDefRE: RegExp) {}

    // for a given targetTemplate, find nested templates
    // add edge from nested templates to targetTemplate
    // as we backtrack from nested template to see if it's edge results into a cycle
    public addEdges = (
        targetTemplateTokens: MarkdownIt.Token[],
        targetTemplateName: string,
        onCycleDetected: (templateName: string) => void
    ): void => {
        targetTemplateTokens.forEach((token: MarkdownIt.Token) => {
            const nestedTemplateName = _extractTemplateName(this._templateDefRE, token.info);
            if (this._edges[nestedTemplateName] === undefined) {
                // initialize array of edges for currentfile
                this._edges[nestedTemplateName] = [targetTemplateName];
            } else if (this._edges[nestedTemplateName].indexOf(targetTemplateName) === -1) {
                // if edge does not exist
                this._edges[nestedTemplateName].push(targetTemplateName);
            }

            if (this._detectCycle(nestedTemplateName)) {
                onCycleDetected(nestedTemplateName);
            }
        });
    };

    private _detectCycle = (startNode: string): boolean => {
        const stack = [startNode];
        const visited: { [key: string]: boolean } = {};
        visited[startNode] = true;

        while (stack.length !== 0) {
            const top = stack.pop();
            if (top && this._edges[top]) {
                for (let i = 0; i < this._edges[top].length; i++) {
                    const node = this._edges[top][i];
                    if (!visited[node]) {
                        stack.push(node);
                        visited[node] = true;
                    } else if (startNode === node) {
                        return true;
                    }
                }
            }
        }

        return false;
    };
}

function _modifyContainerTokens(tokens: MarkdownIt.Token[], idx: number): MarkdownIt.Token[] {
    for (let itr = idx + 1; !(tokens[idx].level === tokens[itr].level && tokens[itr].type === TemplateConstants.TemplateCloseTag); itr++) {
        tokens[itr].type = MarkdownConstants.MaskedTokenType;
    }

    return tokens;
}

function _findStartIndex(tokens: MarkdownIt.Token[], idx: number): number {
    for (let itr = idx - 1; itr >= 0; itr--) {
        if (tokens[idx].level === tokens[itr].level && tokens[itr].type === TemplateConstants.TemplateOpenTag) {
            return itr;
        }
    }

    return -1;
}

function _extractTemplateTokensFromMD(renderer: MarkdownRenderer, templateContent: string): MarkdownIt.Token[] {
    const tokens: MarkdownIt.Token[] = renderer.parse(templateContent);
    const nestedTemplates: MarkdownIt.Token[] = [];
    const templateTokens: MarkdownIt.Token[] = _extractTemplateTokens(tokens);
    templateTokens.forEach((token: MarkdownIt.Token) => {
        nestedTemplates.push(token);
    });
    return nestedTemplates;
}

function _extractTemplateTokens(tokens: MarkdownIt.Token[]): MarkdownIt.Token[] {
    return tokens.filter(token => token.type === TemplateConstants.TemplateOpenTag);
}

function _replaceParameterValues(templateContent: string, templateArgs: string, renderer: MarkdownRenderer): string {
    let tempTemplateContent = templateContent;
    let key: string;
    let value: string;

    const parameters = _parseParameters(templateArgs);
    for (let i = 0; i < parameters.length; i++) {
        if (parameters[i].length > 2) {
            key = parameters[i][1];
            value = parameters[i][2];
        } else {
            key = (i + 1).toString();
            value = parameters[i][1];
        }
        // replace number parameters eg {{{2}}} with 2nd parameters
        tempTemplateContent = tempTemplateContent.replace(new RegExp("\\{\\{\\{" + "\\s*" + key + "\\s*\\}\\}\\}", "g"), value);
        // replace default parameters eg {{{param | defaultvalue}}}
        tempTemplateContent = tempTemplateContent.replace(
            new RegExp("\\{\\{\\{\\s*" + key + "\\s*\\|" + '\\s*\\"(?:[^"\\\\]|\\\\.)*\\"\\s*\\}\\}\\}', "g"),
            value
        );
    }

    tempTemplateContent = _replaceDefaultParameterValues(tempTemplateContent);

    // check if template is injected via parameters
    if (_isArrayEqual(_extractTemplateTokensFromMD(renderer, templateContent), _extractTemplateTokensFromMD(renderer, tempTemplateContent))) {
        return tempTemplateContent;
    }
    return templateContent;
}

function _replaceDefaultParameterValues(templateContent: string): string {
    const paramRE: RegExp = /\{\{\{\s*[0-9a-zA-Z_]+\s*\|([\s\S]*?)\}\}\}/g;
    const templateContentWithDefaultParams = templateContent; // finding and replacing in same string is risky
    let result: RegExpExecArray | null;
    while ((result = paramRE.exec(templateContentWithDefaultParams))) {
        const value: string = result[1].trim();
        if (_isValidValue(value)) {
            templateContent = templateContent.replace(result[0], value.substr(1, value.length - 2));
        }
    }
    return templateContent;
}

function _isValidValue(value: string): boolean {
    if (value.length > 1 && value[0] === '"' && value[value.length - 1] === '"') {
        for (let i = 1; i < value.length - 1; i++) {
            if (value[i] === '"' && value[i - 1] !== "\\") {
                return false;
            }
        }
        return true;
    }
    return false;
}

function _parseParameters(templateArgs: string): RegExpExecArray[] {
    const nameValueParameterRE = /^\s*([0-9a-zA-Z_]+)[\s]*\:[\s]*\"([\s\S]*)\"\s*$/;
    const numberParameterRE = /^\s*\"([\s\S]*)\"\s*$/;
    const parameters: RegExpExecArray[] = [];
    let parameter = "";
    let quoteCount = 0;
    templateArgs = " " + templateArgs; // for checking leading escape char for first char
    for (let i = 1; i < templateArgs.length; i++) {
        // space is appended so starts from i=1
        if (templateArgs[i] === '"' && templateArgs[i - 1] !== "\\") {
            // not escaped " detected
            if (quoteCount < 2) {
                quoteCount += 1;
                parameter += templateArgs[i];
            } else {
                return [];
            }
        } else if (templateArgs[i] === "," && quoteCount === 2) {
            const parameterExecRes = nameValueParameterRE.exec(parameter) || numberParameterRE.exec(parameter);
            if (parameterExecRes) {
                parameters.push(parameterExecRes);
                parameter = "";
                quoteCount = 0;
            } else {
                return [];
            }
        } else {
            parameter += templateArgs[i];
        }
    }
    const parameterExecRes = nameValueParameterRE.exec(parameter) || numberParameterRE.exec(parameter);

    if (parameterExecRes) {
        parameters.push(parameterExecRes); // push last parameter
        return parameters;
    }
    return [];
}

/**
 * For a given line or token.info extract templatename
 * @param line markdown line or token.info
 */
function _extractTemplateName(templateDefRE: RegExp, line: string): string {
    const templateDef = line.match(templateDefRE);
    return templateDef && templateDef.length >= 3 ? _getWellFormedTemplateName(templateDef[2]) : "";
}

// appends .md if not present
function _getWellFormedTemplateName(templateName: string): string {
    templateName = templateName.trim();

    if (templateName !== "" && !endsWith(templateName, FormatConstants.MDFormat, true)) {
        templateName = templateName + FormatConstants.MDFormat;
    }
    return templateName;
}

function _getErrorMessageContent(message: string): string {
    const ele = document.createElement("span");
    ele.className = "secondary-text";
    ele.innerText = message;
    return ele.outerHTML;
}

function _isArrayEqual(arrayA: MarkdownIt.Token[], arrayB: MarkdownIt.Token[]): boolean {
    if (arrayA.length !== arrayB.length) {
        return false;
    }

    for (let i = 0, len = arrayA.length; i < len; ++i) {
        if (arrayA[i].type !== arrayB[i].type) {
            return false;
        }
    }

    return true;
}
